/**
 * Slide accessibility fix (Fragrances Focus)
 * Horizon marks off-screen carousel slides aria-hidden="true", but the links
 * and buttons inside them stay keyboard-focusable. This fails the audit
 * "ARIA hidden element must not be focusable or contain focusable elements".
 *
 * This script watches every <slideshow-slide>. While a slide is aria-hidden,
 * its focusable children get tabindex="-1". When the slide becomes visible,
 * their original tabindex is restored. Mouse clicks and dragging are unaffected.
 */
(() => {
  const FOCUSABLE =
    'a[href], area[href], button, input, select, textarea, iframe, summary, ' +
    '[tabindex], [contenteditable="true"], video[controls], audio[controls]';
  const STORE = 'data-slide-tabindex';
  const HIDDEN_SLIDE = 'slideshow-slide[aria-hidden="true"]';

  /** @param {Element} slide */
  function sync(slide) {
    if (slide.getAttribute('aria-hidden') === 'true') {
      slide.querySelectorAll(FOCUSABLE).forEach((el) => {
        if (el.hasAttribute(STORE)) return;
        el.setAttribute(STORE, el.getAttribute('tabindex') ?? 'none');
        el.setAttribute('tabindex', '-1');
      });
    } else {
      slide.querySelectorAll(`[${STORE}]`).forEach((el) => {
        // Still inside another hidden slide (e.g. nested carousels)? Keep it out of the tab order.
        if (el.parentElement?.closest(HIDDEN_SLIDE)) return;
        const previous = el.getAttribute(STORE);
        el.removeAttribute(STORE);
        if (previous === 'none') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', previous);
      });
    }
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') {
        if (record.target.localName === 'slideshow-slide') sync(record.target);
        continue;
      }
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.localName === 'slideshow-slide') sync(node);
        node.querySelectorAll?.('slideshow-slide').forEach(sync);
        // Content injected into an already-hidden slide (filtering, infinite scroll, etc.)
        const hiddenParent = node.parentElement?.closest(HIDDEN_SLIDE);
        if (hiddenParent) sync(hiddenParent);
      }
    }
  });

  function init() {
    document.querySelectorAll('slideshow-slide').forEach(sync);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();